import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { AuthContext, API } from '../App';
import { MessageSquare, CheckCircle2, AlertCircle, User, LogOut } from 'lucide-react';
import Logo from '../components/Logo';
import axios from 'axios';
import { toast } from 'sonner';

export default function InterviewTestPage() {
  const { user, logout, fetchUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/interview/questions`);
      setQuestions(response.data);
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Free limit reached. Upgrade to continue.');
      } else {
        toast.error('Failed to load questions');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleSubmit = async () => {
    const answersArray = Object.keys(answers).map(questionId => ({
      question_id: questionId,
      answer: answers[questionId]
    }));

    if (answersArray.length < questions.length) {
      toast.error('Please answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/interview/submit`, { answers: answersArray });
      setResult(response.data);
      toast.success('Test submitted successfully!');
      await fetchUser();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Free limit reached. Upgrade to continue.');
      } else {
        toast.error(error.response?.data?.detail || 'Submission failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-[#009639]';
    if (score >= 60) return 'text-yellow-600';
    return 'text-[#CE1126]';
  };

  const isFreePlan = user?.subscription_status !== 'active';
  const canTest = !isFreePlan || (user?.interview_tests_count || 0) < 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#CE1126]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Logo linkTo="/dashboard" />
          <nav className="flex items-center space-x-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm">Dashboard</Button>
            </Link>
            <Link to="/account">
              <Button variant="ghost" size="sm">
                <User className="w-4 h-4 mr-2" />
                Account
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Interview Booster</h1>
          <p className="text-gray-600">Practice English interview questions for UAE employers</p>
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Tests and CV checks are linked to your account email ({user?.email}) and current CV.
            </p>
          </div>
        </div>

        {!canTest && !result ? (
          <Card className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Free Limit Reached</h3>
            <p className="text-gray-600 mb-6">
              You've used your 1 free interview test. Upgrade to Pro for more!
            </p>
            <Link to="/pricing">
              <Button className="btn-uae-red" data-testid="upgrade-test-btn">
                Upgrade to Premium - AED 10/month
              </Button>
            </Link>
          </Card>
        ) : result ? (
          <div className="space-y-6">
            {/* Overall Score */}
            <Card className="p-8 text-center score-card">
              <h2 className="text-2xl font-bold mb-4">Your Score</h2>
              <div className={`text-7xl font-bold mb-4 ${getScoreColor(result.overall_score)}`}>
                {result.overall_score}
              </div>
              <p className="text-gray-600 mb-4">out of 100</p>
              <Button onClick={() => { setResult(null); setAnswers({}); fetchQuestions(); }} variant="outline" data-testid="retake-test-btn">
                Take Another Test
              </Button>
            </Card>

            {/* Category Scores */}
            <Card className="p-6">
              <h3 className="text-xl font-bold mb-6">Category Breakdown</h3>
              <div className="space-y-4">
                {Object.keys(result.category_scores).map(category => (
                  <div key={category}>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium capitalize">{category.replace('_', ' ')}</span>
                      <span className={`font-bold ${getScoreColor(result.category_scores[category])}`}>
                        {result.category_scores[category]}%
                      </span>
                    </div>
                    <Progress value={result.category_scores[category]} className="h-2" />
                  </div>
                ))}
              </div>
            </Card>

            {/* Weak Areas */}
            {result.weak_areas.length > 0 && (
              <Card className="p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <AlertCircle className="w-6 h-6 mr-2 text-yellow-600" />
                  Areas to Improve
                </h3>
                <div className="flex flex-wrap gap-2">
                  {result.weak_areas.map((area, index) => (
                    <span key={index} className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium capitalize">
                      {area.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </Card>
            )}

            {/* Suggestions */}
            <Card className="p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <CheckCircle2 className="w-6 h-6 mr-2 text-[#009639]" />
                Practice Recommendations
              </h3>
              <ul className="space-y-3">
                {result.suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Upgrade CTA */}
            {isFreePlan && (
              <Card className="p-6 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
                <h3 className="text-xl font-bold mb-2">Want Unlimited Practice?</h3>
                <p className="mb-4 opacity-90">
                  Upgrade to Pro for AED 49/month and get unlimited interview practice!
                </p>
                <Link to="/pricing">
                  <Button className="btn-uae-red">
                    Upgrade Now
                  </Button>
                </Link>
              </Card>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Progress */}
            <Card className="p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Progress</span>
                <span className="text-sm text-gray-600">
                  {Object.keys(answers).length} / {questions.length}
                </span>
              </div>
              <Progress value={(Object.keys(answers).length / questions.length) * 100} className="h-2" />
            </Card>

            {/* Questions */}
            {questions.map((question, index) => (
              <Card key={question.id} className="p-6" data-testid={`question-${index}`}>
                <h3 className="font-bold mb-4">
                  {index + 1}. {question.question}
                </h3>
                {question.question_type === 'mcq' ? (
                  <RadioGroup
                    value={answers[question.id] || ''}
                    onValueChange={(value) => handleAnswerChange(question.id, value)}
                  >
                    {question.options.map((option, optIndex) => (
                      <div key={optIndex} className="flex items-center space-x-2 mb-2">
                        <RadioGroupItem value={option} id={`${question.id}-${optIndex}`} data-testid={`question-${index}-option-${optIndex}`} />
                        <Label htmlFor={`${question.id}-${optIndex}`} className="cursor-pointer">
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                ) : (
                  <Input
                    placeholder="Type your answer here..."
                    value={answers[question.id] || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    data-testid={`question-${index}-input`}
                  />
                )}
              </Card>
            ))}

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={submitting || Object.keys(answers).length < questions.length}
              className="w-full btn-uae-green"
              data-testid="submit-test-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Test'}
            </Button>

            {isFreePlan && (
              <p className="text-sm text-gray-500 text-center">
                Free tests remaining: {1 - (user?.interview_tests_count || 0)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
